import { Progress } from "@base-ui/react/progress";
import { CheckCircle2, X } from "lucide-react";
import Button from "../button/Button";

export interface UploadProgressProps {
  progress: number;
  totalFiles: number;
}

export default function UploadProgress({
  progress,
  totalFiles,
}: UploadProgressProps) {
  const isComplete = progress >= 100;

  return (
    <div className="w-[380px] bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
      {/* HEADER */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
        <div className="flex items-center gap-2">
          {isComplete ? (
            <CheckCircle2 className="text-green-500" size={18} />
          ) : (
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
          )}

          <div className="flex flex-col">
            <p className="text-sm font-semibold text-gray-800">
              {isComplete
                ? `Uploaded ${totalFiles} file${totalFiles > 1 ? "s" : ""}`
                : `Uploading ${progress}% of ${totalFiles} file${totalFiles > 1 ? "s" : ""}`}
            </p>

            <p className="text-xs text-gray-500">
              {isComplete
                ? "All files uploaded successfully"
                : `${progress}% completed`}
            </p>
          </div>
        </div>

        <Button
          variant="ghost"
          className="text-gray-400 hover:text-black text-sm cursor-pointer"
        >
          <X size={16} />
        </Button>
      </div>

      {/* PROGRESS BAR + % */}
      <div className="px-4 pb-4 mt-5">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>{progress}%</span>
          <span>
            {progress}% of {totalFiles} file{totalFiles > 1 ? "s" : ""}
          </span>
        </div>

        <Progress.Root value={progress} className="w-full">
          <Progress.Track className="bg-gray-200 h-3 rounded-full overflow-hidden relative">
            {/* PROGRESS */}
            <Progress.Indicator
              className={`h-2 transition-all duration-300 ${
                isComplete ? "bg-green-500" : "bg-blue-500"
              }`}
              style={{ width: `${progress}%` }}
            />

            {/* ANIMATION  */}
            {!isComplete && (
              <div className="absolute inset-0 overflow-hidden">
                <div className="h-full w-[40%] bg-gradient-to-r from-transparent via-white/40 to-transparent animate-[shimmer_1.5s_infinite]" />
              </div>
            )}
          </Progress.Track>
        </Progress.Root>
      </div>
    </div>
  );
}
